import { useCallback, useEffect, useState } from "react";
import api, { apiErr, fmtMoney } from "@/lib/api";
import { PageHeader, Loading, EmptyState, Section8Badge } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Plus, Mail, Phone, Copy, CheckCircle2, FileText, MessageSquareText, RefreshCcw, Send, KeyRound, Trash2, ClipboardCheck, ClipboardList, Smartphone } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DocumentsDialog } from "@/components/DocumentsDialog";
import { DeleteTenantDialog } from "@/components/DeleteTenantDialog";
import { TenantSmsDialog } from "@/components/TenantSmsDialog";
import { PendingInvites, InviteLinkBox } from "@/components/PendingInvites";
import { toast } from "sonner";

const genTempPassword = () => `Welcome-${Math.random().toString(36).slice(2, 8)}${Math.floor(Math.random() * 90 + 10)}`;

function saveLabel(mode, saving) {
  if (saving) return mode === "invite" ? "Sending…" : "Creating…";
  return mode === "invite" ? "Send invitation" : "Create tenant";
}

function AddTenant({ properties, onDone, initial }) {
  const [mode, setMode] = useState("invite");
  const [form, setForm] = useState({
    name: "", email: "", phone: "", password: genTempPassword(),
    property_id: "", total_rent: "", section8: false, hap_amount: "", tenant_portion: "",
    deposit: "", start_date: new Date().toISOString().slice(0, 10), rent_due_day: 1,
    ...(initial || {}),
  });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name || !form.email) return toast.error("Name and email are required");
    setSaving(true);
    try {
      const payload = {
        name: form.name, email: form.email, phone: form.phone,
        property_id: form.property_id || null,
        total_rent: Number(form.total_rent) || 0,
        section8: form.section8,
        hap_amount: Number(form.hap_amount) || 0,
        tenant_portion: form.section8 ? Number(form.tenant_portion) || 0 : Number(form.total_rent) || 0,
        deposit: Number(form.deposit) || 0,
        start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
        rent_due_day: Number(form.rent_due_day) || 1,
      };
      if (mode === "invite") {
        const { data } = await api.post("/invites", { ...payload, send_email: true });
        setCreated({ ...data, mode: "invite" });
        toast.success(data.email_result?.sent ? `Invitation sent to ${data.email}` : "Invite link created");
      } else {
        const { data } = await api.post("/tenants", { ...payload, password: form.password });
        setCreated({ ...data, mode: "password" });
        toast.success("Tenant created");
      }
      onDone();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  if (created?.mode === "invite") {
    return (
      <div className="space-y-4" data-testid="invite-created">
        <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-5 text-center">
          <Send className="h-10 w-10 text-emerald-600 mx-auto mb-2" />
          <h3 className="font-display font-bold text-slate-900">Invitation ready for {created.name}</h3>
          <p className="text-sm text-slate-600 mt-1">{created.name} picks their own password from the link — no temp password to share.{created.property_name ? ` Their ${created.property_name} lease starts the moment they join.` : ""}</p>
        </div>
        <InviteLinkBox url={created.invite_url} emailResult={created.email_result} email={created.email} />
      </div>
    );
  }

  if (created) {
    return (
      <div className="space-y-4" data-testid="tenant-created">
        <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-5 text-center">
          <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto mb-2" />
          <h3 className="font-display font-bold text-slate-900">{created.name} can now log in</h3>
          <p className="text-sm text-slate-600 mt-1">Share these credentials with your tenant.</p>
        </div>
        <div className="rounded-xl border border-slate-200 divide-y">
          <div className="flex items-center justify-between p-3">
            <span className="text-sm text-slate-500">Email</span>
            <span className="text-sm font-semibold text-slate-900">{created.email}</span>
          </div>
          <div className="flex items-center justify-between p-3">
            <span className="text-sm text-slate-500">Temp password</span>
            <button onClick={() => { navigator.clipboard.writeText(created.temp_password); toast.success("Copied"); }}
              className="text-sm font-semibold text-primary flex items-center gap-1.5">
              {created.temp_password} <Copy className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  const bedge = Number(form.total_rent) - Number(form.hap_amount);

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
      <div className="grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1" data-testid="tenant-mode">
        {[["invite", "Send invite link", Send], ["password", "Set a temp password", KeyRound]].map(([k, label, Icon]) => (
          <button key={k} type="button" onClick={() => setMode(k)} data-testid={`tenant-mode-${k}`}
            className={`flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition ${mode === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </div>
      <p className="text-xs text-slate-500 -mt-2" data-testid="tenant-mode-help">
        {mode === "invite" ? "Recommended: the tenant gets an email link, chooses their own password (or uses Google) and the lease below attaches automatically." : "You set a temporary password and share it with the tenant yourself."}
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5 col-span-2"><Label>Full name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="tenant-name" placeholder="Marcus Johnson" /></div>
        <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="tenant-email" placeholder="marcus@example.com" /></div>
        <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(614) 555-0142" /></div>
        {mode === "password" && <div className="space-y-1.5 col-span-2"><Label>Temp password (at least 8 characters — a random one is suggested)</Label><Input value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="tenant-password" /></div>}
      </div>

      <div className="rounded-xl border border-slate-200 p-4 space-y-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Lease (optional)</div>
        <div className="space-y-1.5">
          <Label>Property</Label>
          <Select value={form.property_id} onValueChange={(v) => set("property_id", v)}>
            <SelectTrigger data-testid="tenant-property"><SelectValue placeholder="Assign a property" /></SelectTrigger>
            <SelectContent>
              {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Total monthly rent</Label><Input type="number" value={form.total_rent} onChange={(e) => set("total_rent", e.target.value)} data-testid="tenant-rent" placeholder="1600" /></div>
          <div className="space-y-1.5"><Label>Security deposit</Label><Input type="number" value={form.deposit} onChange={(e) => set("deposit", e.target.value)} placeholder="400" /></div>
        </div>
        <div className="flex items-center justify-between rounded-lg bg-amber-50 border border-amber-100 px-3 py-2.5">
          <div>
            <div className="text-sm font-semibold text-amber-900">Section 8 / Housing voucher</div>
            <div className="text-xs text-amber-700">Split rent into HAP + tenant copay</div>
          </div>
          <Switch checked={form.section8} onCheckedChange={(v) => set("section8", v)} data-testid="tenant-section8" />
        </div>
        {form.section8 && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>HAP (voucher)</Label><Input type="number" value={form.hap_amount} onChange={(e) => set("hap_amount", e.target.value)} data-testid="tenant-hap" placeholder="1200" /></div>
            <div className="space-y-1.5"><Label>Tenant copay</Label><Input type="number" value={form.tenant_portion} onChange={(e) => set("tenant_portion", e.target.value)} data-testid="tenant-copay" placeholder="400" /></div>
            {form.total_rent && form.hap_amount && bedge !== Number(form.tenant_portion) && (
              <p className="col-span-2 text-xs text-amber-700">Tip: HAP + copay should equal {fmtMoney(form.total_rent)} (copay ≈ {fmtMoney(bedge)}).</p>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Lease start</Label><Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Rent due day</Label><Input type="number" min="1" max="28" value={form.rent_due_day} onChange={(e) => set("rent_due_day", e.target.value)} /></div>
        </div>
      </div>

      <DialogFooter>
        <Button onClick={save} disabled={saving} data-testid="tenant-save" className="w-full gap-2">
          {mode === "invite" && <Send className="h-4 w-4" />}{saveLabel(mode, saving)}
        </Button>
      </DialogFooter>
    </div>
  );
}

export default function Tenants() {
  const [tenants, setTenants] = useState(null);
  const [invites, setInvites] = useState([]);
  const [properties, setProperties] = useState([]);
  const [open, setOpen] = useState(false);
  const [docsFor, setDocsFor] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);
  const [smsFor, setSmsFor] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => {
    api.get("/tenants").then((r) => setTenants(r.data));
    api.get("/properties").then((r) => setProperties(r.data));
    api.get("/invites").then((r) => setInvites(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const inviteId = params.get("invite");
  useEffect(() => {
    if (!inviteId) return;
    api.get(`/applications/${inviteId}`).then((r) => {
      const a = r.data;
      setPrefill({ name: a.name, email: a.email, phone: a.phone || "", property_id: a.property_id, total_rent: a.target_rent || "",
        start_date: a.move_in_date || new Date().toISOString().slice(0, 10) });
      setOpen(true);
    }).catch((e) => toast.error(apiErr(e)));
  }, [inviteId]);

  const onOpenChange = (o) => { setOpen(o); if (!o && inviteId) { setPrefill(null); setParams({}, { replace: true }); } };

  if (!tenants) return <Loading />;

  return (
    <div>
      <PageHeader title="Tenants" subtitle="Residents, their logins, and lease breakdowns."
        action={
          <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogTrigger asChild><Button className="gap-2" data-testid="add-tenant-btn"><Plus className="h-4 w-4" /> Add tenant</Button></DialogTrigger>
            <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>{prefill ? `Invite ${prefill.name} as a tenant` : "Add a tenant"}</DialogTitle></DialogHeader>
              <AddTenant key={prefill?.email || "new"} properties={properties} onDone={load} initial={prefill} />
            </DialogContent>
          </Dialog>
        }
      />

      <PendingInvites invites={invites} onChange={load} />

      {tenants.length === 0 ? (
        <EmptyState icon={Users} title="No tenants yet" subtitle="Invite a tenant by email link or create a login and assign them to a property." testid="tenants-empty"
          action={<Button onClick={() => setOpen(true)} className="gap-2"><Plus className="h-4 w-4" /> Add tenant</Button>} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {tenants.map((t) => (
            <div key={t.id} className="relative rounded-2xl border border-slate-200 bg-white p-5 card-hover" data-testid={`tenant-card-${t.id}`}>
              <button type="button" onClick={() => setDeleteFor(t)} title={`Delete ${t.name}`} aria-label={`Delete ${t.name}`}
                className="absolute top-3 right-3 h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                data-testid={`tenant-delete-${t.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
              <div className="flex items-center gap-3 pr-8">
                <div className="h-11 w-11 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-bold">
                  {t.name?.slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <h3 className="font-display font-bold text-slate-900 truncate">{t.name}</h3>
                  <div className="text-xs text-slate-500 flex items-center gap-1 truncate"><Mail className="h-3 w-3" />{t.email}</div>
                </div>
              </div>
              {t.phone && <div className="text-xs text-slate-500 flex items-center gap-1 mt-2"><Phone className="h-3 w-3" />{t.phone}{t.sms_ready && <span className="ml-1 rounded-full bg-emerald-100 text-emerald-800 px-1.5 py-0.5 text-[10px] font-bold" data-testid={`tenant-sms-badge-${t.id}`}>TEXTS ON</span>}</div>}
              {t.lease ? (
                <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-3.5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-slate-800 truncate">{t.lease.property?.name}</span>
                    <Section8Badge section8={t.lease.section8} />
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">Monthly rent</span>
                    <span className="font-semibold text-slate-900">{fmtMoney(t.lease.total_rent)}</span>
                  </div>
                  {t.lease.section8 && (
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div className="rounded-lg bg-amber-50 px-2 py-1.5 text-center">
                        <div className="text-[10px] font-semibold uppercase text-amber-700">HAP</div>
                        <div className="text-sm font-bold text-amber-900">{fmtMoney(t.lease.hap_amount)}</div>
                      </div>
                      <div className="rounded-lg bg-indigo-50 px-2 py-1.5 text-center">
                        <div className="text-[10px] font-semibold uppercase text-indigo-700">Copay</div>
                        <div className="text-sm font-bold text-indigo-900">{fmtMoney(t.lease.tenant_portion)}</div>
                      </div>
                    </div>
                  )}
                  {t.move_in && (
                    <button type="button" onClick={() => setDocsFor(t)} data-testid={`tenant-movein-badge-${t.id}`}
                      className={`mt-2 mr-1.5 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${t.move_in.status === "submitted" ? "bg-indigo-100 text-indigo-800 hover:bg-indigo-200" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                      <ClipboardCheck className="h-3 w-3" />
                      {t.move_in.status === "submitted" ? `Move-in checklist · ${new Date(t.move_in.submitted_at).toLocaleDateString()}`
                        : t.move_in.status === "draft" ? `Move-in checklist · ${t.move_in.rated}/${t.move_in.total} rated` : "Move-in checklist · not started"}
                    </button>
                  )}
                  {t.move_out && t.move_out.status !== "none" && (
                    <button type="button" onClick={() => navigate(`/tenants/${t.id}/move-out`)} data-testid={`tenant-moveout-badge-${t.id}`}
                      className={`mt-2 mr-1.5 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${t.move_out.status === "submitted" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200" : "bg-amber-100 text-amber-800 hover:bg-amber-200"}`}>
                      <ClipboardList className="h-3 w-3" />
                      {t.move_out.status === "submitted" ? `Move-out statement · ${new Date(t.move_out.submitted_at).toLocaleDateString()}${t.move_out.has_comment ? " · tenant commented" : ""}` : "Move-out inspection · in progress"}
                    </button>
                  )}
                  {t.lease.autopay?.active && (
                    <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-800 px-2.5 py-1 text-[11px] font-semibold" data-testid={`tenant-autopay-badge-${t.id}`}>
                      <RefreshCcw className="h-3 w-3" /> Auto-pay on · {fmtMoney(t.lease.autopay.amount)} on the {t.lease.autopay.due_day}{["st","nd","rd"][((t.lease.autopay.due_day + 90) % 100 - 10) % 10 - 1] || "th"}
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-4 rounded-xl border border-dashed border-slate-300 p-3 text-center text-xs text-slate-500">No active lease</div>
              )}
              <div className="flex gap-2 mt-3">
                <Button variant="outline" size="sm" className="flex-1 gap-1.5" onClick={() => setDocsFor(t)} data-testid={`tenant-docs-${t.id}`}>
                  <FileText className="h-3.5 w-3.5" /> Documents
                </Button>
                <Button variant="outline" size="sm" className="flex-1 gap-1.5" onClick={() => navigate(`/inbox?tenant=${t.id}`)} data-testid={`tenant-chat-${t.id}`}>
                  <MessageSquareText className="h-3.5 w-3.5" /> Message
                </Button>
              </div>
              <div className="flex gap-2 mt-2">
                <Button variant="outline" size="sm" className="flex-1 gap-1.5" onClick={() => setSmsFor(t)} data-testid={`tenant-sms-${t.id}`}>
                  <Smartphone className="h-3.5 w-3.5" /> Phone & texts
                </Button>
                {t.lease && (
                  <Button variant="outline" size="sm" className="flex-1 gap-1.5" onClick={() => navigate(`/tenants/${t.id}/move-out`)} data-testid={`tenant-moveout-${t.id}`}>
                    <ClipboardList className="h-3.5 w-3.5" /> Move-out
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <DocumentsDialog tenant={docsFor} open={!!docsFor} onOpenChange={(o) => !o && setDocsFor(null)} />
      <DeleteTenantDialog tenant={deleteFor} open={!!deleteFor} onOpenChange={(o) => !o && setDeleteFor(null)} onDeleted={load} />
      <TenantSmsDialog tenant={smsFor} open={!!smsFor} onOpenChange={(o) => !o && setSmsFor(null)} onSaved={load} />
    </div>
  );
}
